import { DOCUMENT, Injectable, inject, type OnDestroy } from '@angular/core';
import {
    BehaviorSubject,
    distinctUntilChanged,
    type Observable,
    type Subscription,
    tap,
    timer,
} from 'rxjs';

import { NOTIFICATION_PATH } from '@app/shared/constants/api-route.config';
import type { NotificationAdapter } from '@app/shared/models/notification.model';
import { AppConfigService } from '@app/shared/services/app-config.service';
import type { ItemResponse, ListResponse } from '@app/shared/services/mixins/crud.service';
import { RestService } from '@app/shared/services/rest.service';

@Injectable({
    providedIn: 'root',
})
export class NotificationService implements OnDestroy {
    private readonly endpoint = NOTIFICATION_PATH;

    private notificationSubscriber: Subscription;

    private readonly unreadNotificationCountSubject$ = new BehaviorSubject('');

    private readonly api = inject(RestService);
    private readonly document = inject(DOCUMENT);

    readonly unreadNotificationCount$ = this.unreadNotificationCountSubject$
        .asObservable()
        .pipe(distinctUntilChanged());

    constructor() {
        this.refreshUnreadNotificationCount();
        this.setTimerForNotifications(
            inject(AppConfigService).appConfig().notificationPullInterval || 60000,
        );
    }

    getDataList(param: {
        skip?: number;
        take?: number;
        isViewed?: boolean;
        isStarred?: boolean;
        keyword?: string;
        template?: string[];
        objectType?: string[];
        sortByOrder?: string;
        type?: string[];
    }): Observable<ListResponse<NotificationAdapter>> {
        return this.api.getWithParams(this.endpoint, {
            ...param,
        });
    }

    getNotification(notificationId: number): Observable<ItemResponse<NotificationAdapter>> {
        return this.api.get(`${this.endpoint}${notificationId}`);
    }

    toggleNotificationsAsRead(notifications: {
        notifications: number[];
        read: boolean;
    }): Observable<ItemResponse<{ message: string }>> {
        return this.api.post(`${this.endpoint}toggle_read`, notifications);
    }

    markNotificationAsRead(
        notification: NotificationAdapter,
    ): Observable<ItemResponse<NotificationAdapter>> {
        return this.api.post(`${this.endpoint}${notification.id}`);
    }

    starredNotificationCount(): Observable<ItemResponse<{ starred: number }>> {
        return this.api.get(`${this.endpoint}starred`);
    }

    toggleNotificationStarred(
        id: number,
        payload: { starred: boolean },
    ): Observable<ItemResponse<NotificationAdapter>> {
        return this.api.post(`${this.endpoint}starred/${id}`, payload);
    }

    private getUnreadNotificationCount(): Observable<ItemResponse<{ unread: number }>> {
        return this.api.get(`${this.endpoint}unread`);
    }

    private setTimerForNotifications(notificationPullInterval: number) {
        const callbackFunction = (): void => {
            // Polling should stop when user is not interacting with chrome, like switching to other application.
            // document.hasFocus() is set to true only when user is interacting to the document
            if (!this.document.hasFocus()) return;

            this.refreshUnreadNotificationCount();
        };
        const pollNotifications = () => {
            return timer(notificationPullInterval).pipe(tap(callbackFunction)).subscribe();
        };

        this.notificationSubscriber = pollNotifications();

        // stops polling for inactive tabs:
        this.document.addEventListener('visibilitychange', () => {
            this.notificationSubscriber.unsubscribe();
            if (!this.document.hidden) {
                this.notificationSubscriber = pollNotifications();
            }
        });
    }

    refreshUnreadNotificationCount() {
        this.getUnreadNotificationCount().subscribe(response => {
            const count = response.result.unread || 0;
            if (count) {
                this.unreadNotificationCountSubject$.next(count > 99 ? '99+' : count.toString());
            } else {
                this.unreadNotificationCountSubject$.next('');
            }
        });
    }

    onClearAllNotifications(): Observable<ListResponse<NotificationAdapter>> {
        return this.api.post(`${this.endpoint}mark_all_as_read`);
    }

    ngOnDestroy() {
        this.notificationSubscriber.unsubscribe();
    }
}
